import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  IonHeader,
  IonPage,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonTitle,
  IonRow,
  IonContent,
  useIonViewDidLeave,
} from '@ionic/react'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { add, chevronBackOutline } from 'ionicons/icons'
import { useHistory } from 'react-router'
import { setBodySelectionMode, setSelectedExam, setShowExamModal } from '../store/bodyAssessment.actions'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import useDeviceMetrics from '../../../../utilities/useDeviceMetrics'
import LockPortraitScreenComponent from '../../../../components/lockPortraitScreen/LockPortraitScreen.component'
import {
  setBOD,
  setChestLungMode,
  setDocumentFindings,
  setEnabledZoom,
  setExaminationInProgress,
  setSelectedBodyPart,
  setSelectedBodyPartData,
  setZoomedBodyPart,
} from '../bodySurvey/store/bodySurvey.actions'
import { getBodyAssessmentSelector } from '../selectors'
import { setSelectedAssessment } from '../../store/additionalAssessments.actions'
import { getDocumentedLocations } from '../../../../utilities/bodySurvey.util'
import { chestLungModeData } from '../bodySurvey/bodyParts/BodyZoomView.content'
import { BodyAssessmentConstants } from '../store/bodyAssessment.constants'
import { getBodySurveySelector } from '../bodySurvey/selectors'
import ExamModal from '../examModal/ExamModal.component'
import BodyAssessmentMobile from './bodyAssessmentMobile/BodyAssessmentMobile.content'
import BodyAssessmentTablet from './bodyAssessmentTablet/BodyAssessmentTablet.content'
import ExamBarcomponent from '../examBar/ExamBar.component'
import { compareObjectsIfEqual } from '../../../../utilities/object.util'
import { initialAdditionalBurnInformation } from '../burns/store/burns.reducers'
import { initialDrainsAndTubesState } from '../drainsTubes/store/drainsAndTubes.reducers'
import { initialObstetricsState } from '../obstetrics/store/obstetrics.reducers'
import { getDrainsAndTubesSelector } from '../drainsTubes/selectors'
import { getBurnsSelector } from '../burns/selectors'
import { getObstetricsSelector } from '../obstetrics/selectors'
import { IDrainsAndTubesState } from '../drainsTubes/store/drainsAndTubes.types'
import { IBurnsState } from '../burns/store/burns.types'
import { IObstetricsState } from '../obstetrics/store/obstetrics.types'
import './BodyAssessment.page.css'
import { goBack } from '../../../../utilities/navigation.util'

const BodyAssessmentPage: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const { isTablet, isMobile, orientation } = useDeviceMetrics()
  const bodyAssessment = useSelector(getBodyAssessmentSelector)
  const bodySurvey = useSelector(getBodySurveySelector)
  const drainsAndTubesState: IDrainsAndTubesState = useSelector(getDrainsAndTubesSelector)
  const burnsTbsaState: IBurnsState = useSelector(getBurnsSelector)
  const obstetricsState: IObstetricsState = useSelector(getObstetricsSelector)

  const { seq, examValues, examValuesPNNVs, findings, __enabledZoom, __documentFindings, __selectedBodyParts } =
    bodySurvey
  const { selectedExam } = bodyAssessment

  const combinedExamOptions = [...examValues, ...examValuesPNNVs]
  const allDocumentedLocations = getDocumentedLocations(combinedExamOptions, findings)
  const additionalBurnInformationData: any = burnsTbsaState.additionalBurnInformation

  const hasBurnsData = burnsTbsaState.burnsExamData.length !== 0
  const hasAdditionalBurnInfoChanged = !compareObjectsIfEqual(
    initialAdditionalBurnInformation,
    additionalBurnInformationData
  )

  const burnsDataChanged =
    (burnsTbsaState.isTBSA === 1 || burnsTbsaState.isTBSA === 0) && (hasBurnsData || hasAdditionalBurnInfoChanged)

  const drainsDataChanged = !compareObjectsIfEqual(initialDrainsAndTubesState as any, drainsAndTubesState as any)
  const obstetricsDataChanged = !compareObjectsIfEqual(initialObstetricsState as any, obstetricsState as any)

  let summaryCount = allDocumentedLocations.length

  if (burnsDataChanged) {
    summaryCount += 1
  }

  if (drainsDataChanged) {
    summaryCount += 1
  }

  if (obstetricsDataChanged) {
    summaryCount += 1
  }

  useIonViewDidLeave(() => {
    dispatch(setBOD())
  })

  const resetCommonStates = () => {
    dispatch(setSelectedBodyPartData([]))
    dispatch(setDocumentFindings(false))
    dispatch(setExaminationInProgress(false))
  }

  const resetZoomStates = () => {
    dispatch(setZoomedBodyPart(null))
    dispatch(setEnabledZoom(false))
    dispatch(setSelectedBodyPart(null))
    dispatch(setChestLungMode(chestLungModeData[0]))
    resetCommonStates()
  }

  const onBackClick = () => {
    const zoomedBodyPartsExists = __selectedBodyParts.some((part) => part.isZoomedPart)
    const isBodySurvey = selectedExam === BodyAssessmentConstants.BODY_SURVEY
    const bodySurveyDefault = !__enabledZoom && !__documentFindings && isBodySurvey
    const bodySurveyDocumentation = !__enabledZoom && __documentFindings && isBodySurvey

    if (zoomedBodyPartsExists) {
      dispatch(setEnabledZoom(true))
    }
    if (bodySurveyDefault) {
      goBack()
    } else if (bodySurveyDocumentation) {
      resetCommonStates()
    } else if (__enabledZoom) {
      if (__documentFindings) {
        resetCommonStates()
      } else {
        resetZoomStates()
      }
    } else if (!isBodySurvey) {
      dispatch(setSelectedExam(BodyAssessmentConstants.BODY_SURVEY))
      dispatch(setEnabledZoom(false))
      dispatch(setSelectedBodyPartData([]))
      dispatch(setDocumentFindings(false))
      dispatch(setBodySelectionMode({ id: 1, name: 'Body parts' }))
    } else {
      goBack()
    }
  }

  const onAddExamsClick = () => {
    dispatch(setShowExamModal(true))
  }

  const onSummaryBtnClick = () => {
    history.push(TabConstants.ADDITIONAL_ASSESSMENT_SUMMARY)
    dispatch(setSelectedAssessment(bodySurvey))
  }

  return (
    <>
      <IonPage id='BodyAssessmentPage'>
        <PCRHeader />
        <IonHeader className='ion-no-padding'>
          <IonToolbar color='light' id='additional-assessment-exam-header'>
            <IonButtons slot='start'>
              <IonButton color='primary-alt' data-testid='BackButton' onClick={onBackClick}>
                <IonIcon icon={chevronBackOutline} />
                {t('common.buttons.Back')}
              </IonButton>
            </IonButtons>
            <IonButtons slot='end'>
              {isTablet && !isMobile ? (
                <IonButton
                  data-testid='SummaryBtnTablet'
                  className='btn btn-primary summary-btn-tablet'
                  onClick={onSummaryBtnClick}>
                  {t('additionalAssessments.Summary')} {'(' + summaryCount + ')'}
                </IonButton>
              ) : (
                !isTablet &&
                orientation !== 'landscape' && (
                  <IonButton
                    data-testid='CreateAssessmentTablet'
                    className='btn btn-primary'
                    id='exam-screen-btn'
                    onClick={onAddExamsClick}>
                    <IonIcon icon={add} /> {t('additionalAssessments.Exams')}
                  </IonButton>
                )
              )}
            </IonButtons>
            <IonTitle>
              {t('additionalAssessments.Assessment')} {seq}
            </IonTitle>
          </IonToolbar>
          {!isTablet && orientation !== 'landscape' && (
            <IonRow className='ion-padding' id='additional-assessment-exam-bar'>
              <ExamBarcomponent />
            </IonRow>
          )}
        </IonHeader>
        {isMobile && orientation === 'landscape' ? (
          <IonContent>
            <LockPortraitScreenComponent />
          </IonContent>
        ) : isTablet ? (
          <BodyAssessmentTablet />
        ) : (
          <BodyAssessmentMobile summaryCount={summaryCount} />
        )}
      </IonPage>
      <ExamModal />
    </>
  )
}
export default BodyAssessmentPage
